/**
 * Search & Retrieve — the end-user document surface.
 *
 * Facets come from each container's metadata model, so a field added to a model
 * (or mapped by an archiving pipeline) appears here with no code change.
 */

import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconFilterOff, IconInbox, IconSearch } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  listArchiveContainers,
  listArchiveFacets,
  searchArchive,
  type ArchiveContainer,
  type ArchiveFacet,
  type ArchiveSearchHit,
  type ArchiveSearchOut,
} from "@/shared/api/archive";
import { ArchiveAskPanel, type Interpreted } from "./ArchiveAskPanel";
import { ArchiveObjectDrawer } from "./ArchiveObjectDrawer";
import { clusterColour, clusterTitle, formatBytes, formatCount, formatDate } from "./format";

export function ArchiveSearchPage() {
  const getToken = useAccessToken();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [debounced] = useDebouncedValue(query, 250);
  const [containerId, setContainerId] = useState<string | null>(params.get("container"));
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState("relevance");
  const [page, setPage] = useState(1);

  const [containers, setContainers] = useState<ArchiveContainer[]>([]);
  const [facets, setFacets] = useState<ArchiveFacet[]>([]);
  const [results, setResults] = useState<ArchiveSearchOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openObject, setOpenObject] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        const page_ = await listArchiveContainers(token);
        // Every archive the actor can reach — including dataset archives. The
        // selector picks an *archive*, never a table inside one.
        setContainers(page_.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [getToken]);

  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        setFacets(await listArchiveFacets(token, containerId));
      } catch {
        setFacets([]);
      }
    })();
  }, [getToken, containerId]);

  const runSearch = useCallback(async () => {
    try {
      const token = await getToken();
      setResults(
        await searchArchive(token, {
          q: debounced,
          containerId,
          facets: selected,
          sort,
          page,
          pageSize: 25,
        }),
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getToken, debounced, containerId, selected, sort, page]);

  useEffect(() => {
    void runSearch();
  }, [runSearch]);

  function toggleFacet(key: string, value: string) {
    setPage(1);
    setSelected((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const updated = { ...prev, [key]: next };
      if (!next.length) delete updated[key];
      return updated;
    });
  }

  function clearAll() {
    setSelected({});
    setQuery("");
    setContainerId(null);
    setPage(1);
  }

  /**
   * Turn what the model understood into the same state a person would have set
   * by hand — scope, facets, free text — so the results come from the ordinary
   * search path with the ordinary grants applied.
   *
   * Filters are only applied where they name a facet the current scope actually
   * offers. A model naming a column that is not facetable would otherwise
   * silently return nothing, which reads as "the archive is empty" rather than
   * "that filter does not exist here".
   */
  function applyInterpreted(interpreted: Interpreted) {
    const scope = interpreted.container_ids?.[0] ?? null;
    if (scope) setContainerId(scope);

    const facetKeys = new Set(facets.map((f) => f.key));
    const next: Record<string, string[]> = {};
    for (const filter of interpreted.filters ?? []) {
      if (filter.op !== "eq" || !facetKeys.has(filter.column)) continue;
      next[filter.column] = [...(next[filter.column] ?? []), String(filter.value)];
    }
    setSelected(next);
    setQuery(interpreted.free_text ?? "");
    setPage(1);
  }

  const chips = useMemo(
    () =>
      Object.entries(selected).flatMap(([key, values]) => values.map((value) => ({ key, value }))),
    [selected],
  );
  const hasFilters = chips.length > 0 || !!containerId || !!debounced.trim();

  return (
    <Stack gap="lg">
      <div>
        <Title order={3}>Search &amp; Retrieve</Title>
        <Text size="sm" c="dimmed" mt={4}>
          Search across every archive you have access to — metadata and full document text.
        </Text>
      </div>

      {error && (
        <Alert color="red" variant="light" title="Search failed">
          {error}
        </Alert>
      )}

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Stack gap="md">
            <Paper withBorder p="md" radius="md">
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>
                Archive
              </Text>
              <Select
                placeholder="All archives"
                clearable
                searchable
                value={containerId}
                data={containers.map((c) => ({
                  value: c.container_id,
                  label: `${c.name} — ${clusterTitle(c.cluster)}`,
                }))}
                onChange={(value) => {
                  setContainerId(value);
                  setSelected({});
                  setPage(1);
                }}
              />
              <Text size="xs" c="dimmed" mt={6}>
                {containerId
                  ? "Searching one archive."
                  : `Searching all ${containers.length} archives you have access to.`}
              </Text>
            </Paper>

            {facets.map((facet) => (
              <Paper key={facet.key} withBorder p="md" radius="md">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
                  {facet.label}
                </Text>
                <ScrollArea.Autosize mah={190}>
                  <Stack gap={6}>
                    {facet.values.map((entry) => (
                      <Checkbox
                        key={entry.value}
                        size="xs"
                        checked={(selected[facet.key] ?? []).includes(entry.value)}
                        onChange={() => toggleFacet(facet.key, entry.value)}
                        label={
                          <Group gap={6} wrap="nowrap" justify="space-between" w="100%">
                            <Text size="xs" lineClamp={1}>
                              {entry.value}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatCount(entry.count)}
                            </Text>
                          </Group>
                        }
                      />
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              </Paper>
            ))}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 9 }}>
          <Stack gap="md">
            <ArchiveAskPanel
              containerId={containerId}
              onApply={applyInterpreted}
              onBrowseRecords={(target) => navigate(`/archive/containers/${target}/datasets`)}
            />

            <Group gap="sm" wrap="nowrap">
              <TextInput
                flex={1}
                size="md"
                leftSection={<IconSearch size={17} />}
                placeholder="Search titles and full document text — try “temperature excursion”"
                value={query}
                onChange={(event) => {
                  setQuery(event.currentTarget.value);
                  setPage(1);
                }}
              />
              <Select
                w={180}
                size="md"
                value={sort}
                onChange={(value) => setSort(value ?? "relevance")}
                data={[
                  { value: "relevance", label: "Most relevant" },
                  { value: "effective_date", label: "Effective date" },
                  { value: "ingested_at", label: "Recently archived" },
                ]}
              />
            </Group>

            <Group justify="space-between" mih={26}>
              <Group gap={6}>
                {results && (
                  <Text size="sm" c="dimmed">
                    {formatCount(results.total)} {results.total === 1 ? "record" : "records"}
                  </Text>
                )}
                {chips.map((chip) => (
                  <Badge
                    key={`${chip.key}-${chip.value}`}
                    variant="light"
                    size="sm"
                    tt="none"
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleFacet(chip.key, chip.value)}
                  >
                    {chip.value} ✕
                  </Badge>
                ))}
              </Group>
              {hasFilters && (
                <Button
                  variant="subtle"
                  size="compact-sm"
                  leftSection={<IconFilterOff size={14} />}
                  onClick={clearAll}
                >
                  Clear
                </Button>
              )}
            </Group>

            {!results && !error && (
              <Stack gap="xs">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} height={92} radius="md" />
                ))}
              </Stack>
            )}

            {results?.items.length === 0 && (
              <Paper withBorder p="xl" radius="md">
                <Stack align="center" gap="xs">
                  <IconInbox size={30} color="var(--mantine-color-dimmed)" />
                  <Text fw={500}>No records match</Text>
                  <Text size="sm" c="dimmed" ta="center" maw={420}>
                    Try removing a filter, or widen the archive selection to search everything you
                    have access to.
                  </Text>
                  {hasFilters && (
                    <Button variant="light" size="xs" onClick={clearAll}>
                      Clear filters
                    </Button>
                  )}
                </Stack>
              </Paper>
            )}

            <Stack gap="xs">
              {results?.items.map((hit) => (
                <ResultRow
                  key={hit.object_id}
                  hit={hit}
                  onOpen={() => setOpenObject(hit.object_id)}
                />
              ))}
            </Stack>

            {results && results.total > results.page_size && (
              <Group justify="center" mt="sm">
                <Pagination
                  size="sm"
                  value={page}
                  onChange={setPage}
                  total={Math.ceil(results.total / results.page_size)}
                />
              </Group>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      <ArchiveObjectDrawer objectId={openObject} onClose={() => setOpenObject(null)} />
    </Stack>
  );
}

function ResultRow({ hit, onOpen }: { hit: ArchiveSearchHit; onOpen: () => void }) {
  const meta = (hit.metadata ?? {}) as Record<string, string>;
  return (
    <Card withBorder padding="md" radius="md" onClick={onOpen} style={{ cursor: "pointer" }}>
      <Box style={{ minWidth: 0 }}>
        <Group gap={6} mb={4}>
          <Badge size="sm" variant="light" color="teal" tt="none">
            {hit.object_type}
          </Badge>
          <Badge size="sm" variant="default" color={clusterColour(hit.container_cluster)} tt="none">
            {hit.container_name}
          </Badge>
          {hit.legal_hold && (
            <Badge size="sm" color="orange" variant="light" tt="none">
              Legal hold
            </Badge>
          )}
        </Group>

        <Text fw={500} lineClamp={1}>
          {hit.title}
        </Text>

        {hit.snippet ? (
          // Snippet HTML comes from Postgres ts_headline over our own corpus.
          // Customer content would need sanitising before this is production code.
          <Text
            size="sm"
            c="dimmed"
            mt={6}
            style={{ lineHeight: 1.55 }}
            dangerouslySetInnerHTML={{ __html: hit.snippet }}
          />
        ) : (
          <Text size="sm" c="dimmed" mt={6} lineClamp={1}>
            {hit.source_system} · {hit.source_id}
          </Text>
        )}

        <Group gap="lg" mt={8}>
          {["site", "product", "effective_date", "retention_class"]
            .filter((key) => meta[key])
            .map((key) => (
              <Text key={key} size="xs" c="dimmed">
                {key === "effective_date" ? formatDate(meta[key]) : meta[key]}
              </Text>
            ))}
          <Text size="xs" c="dimmed">
            {formatBytes(hit.size_bytes)}
            {hit.page_count ? ` · ${hit.page_count} pages` : ""}
          </Text>
        </Group>
      </Box>
    </Card>
  );
}
